import StaticPageShell from "@/components/static_pages/StaticPageShell";

export default function PrivacyPolicy() {
  return (
    <StaticPageShell title="Privacy Policy">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-gray-900">Privacy Policy</h1>
        <p className="text-gray-600">Last updated: August 2026</p>
      </div>

      <div className="prose prose-gray max-w-none">
        <p className="mb-6 text-lg text-gray-700">
          ScoutUX ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we
          collect, use, disclose, and safeguard your information when you use our AI-powered usability testing service.
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">1. Information We Collect</h2>

        <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">1.1 Information You Provide</h3>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>
            <strong>Account Information:</strong> When you create an account using Google OAuth, we collect your name,
            email address, and profile information provided by Google.
          </li>
          <li>
            <strong>Website Information:</strong> URLs and configuration details of websites you submit for usability
            testing.
          </li>
          <li>
            <strong>Persona Data:</strong> User personas you create for testing scenarios.
          </li>
          <li>
            <strong>Payment Information:</strong> Billing information processed through Stripe (we do not store your
            payment details directly).
          </li>
        </ul>

        <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">1.2 Information We Collect Automatically</h3>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>
            <strong>Usage Data:</strong> Information about how you use our service, including interactions, features
            accessed, and time spent.
          </li>
          <li>
            <strong>Technical Data:</strong> IP address, browser type, device information, and operating system.
          </li>
          <li>
            <strong>Analytics Data:</strong> We use Google Analytics and PostHog to understand user behavior and improve
            our service.
          </li>
          <li>
            <strong>Screenshots and Recordings:</strong> Our AI-powered browser interactions capture screenshots and
            interaction data from the websites you test.
          </li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">2. How We Use Your Information</h2>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>Provide and maintain our usability testing service</li>
          <li>Generate AI-powered usability audits based on Nielsen's heuristics</li>
          <li>Process payments and manage subscriptions through Stripe</li>
          <li>Send service-related communications</li>
          <li>Improve our service through analytics and user feedback</li>
          <li>Ensure security and prevent fraud</li>
          <li>Comply with legal obligations</li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">3. AI and External Services</h2>
        <p className="mb-4 text-gray-700">
          We use Google's Gemini AI models to analyze your websites and generate usability audits. This processing may
          involve:
        </p>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>Sending website screenshots and interaction data to Google's Gemini AI service</li>
          <li>Processing this data to identify usability issues and generate recommendations</li>
          <li>On the free plan, your data may be used by Google to improve their AI models</li>
          <li>On the paid plans, your data is not used to train any AI models</li>
          <li>AI processing is governed by Google's privacy policies and our data processing agreements</li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">4. Data Storage and Security</h2>
        <p className="mb-4 text-gray-700">We implement appropriate technical and organizational security measures:</p>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>
            <strong>Cloud Storage:</strong> Screenshots and files are stored securely in third party cloud storage
          </li>
          <li>
            <strong>Database Security:</strong> User data is stored in database services on the cloud
          </li>
          <li>
            <strong>Access Controls:</strong> Multi-tenant architecture ensures data isolation between accounts
          </li>
          <li>
            <strong>Regular Backups:</strong> Data is regularly backed up to prevent loss
          </li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">5. Data Sharing and Disclosure</h2>
        <p className="mb-4 text-gray-700">We may share your information with:</p>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>
            <strong>Service Providers:</strong> Google (OAuth and AI), Stripe (payments), Amazon Web Services (hosting),
            PostHog (analytics), Google Analytics
          </li>
          <li>
            <strong>Legal Requirements:</strong> When required by law or to protect our rights
          </li>
          <li>
            <strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales
          </li>
        </ul>
        <p className="mt-4 text-gray-700">
          We do not sell, rent, or trade your personal information to third parties for marketing purposes.
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">6. Data Retention</h2>
        <p className="text-gray-700">
          We retain your information for as long as necessary to provide our services and comply with legal obligations.
          You may request deletion of your account and associated data at any time by contacting support@scoutux.io.
          Note that payment information stored by Stripe may be retained according to their policies and legal
          requirements.
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">7. Your Rights</h2>
        <p className="mb-4 text-gray-700">Depending on your location, you may have the following rights:</p>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>Access and portability of your personal data</li>
          <li>Correction of inaccurate data</li>
          <li>Deletion of your data (subject to legal obligations)</li>
          <li>Restriction of processing</li>
          <li>Withdrawal of consent</li>
        </ul>
        <p className="mt-4 text-gray-700">To exercise these rights, contact us at support@scoutux.io.</p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">8. Cookies and Tracking</h2>
        <p className="text-gray-700">
          We use cookies and similar technologies for authentication, preferences, and analytics. Our analytics
          providers (Google Analytics, PostHog) may set their own cookies. You can control cookie preferences through
          your browser settings.
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">9. International Data Transfers</h2>
        <p className="text-gray-700">
          Your data may be processed in countries other than your own. We ensure appropriate safeguards are in place for
          international transfers, including standard contractual clauses and adequacy decisions.
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">10. Children's Privacy</h2>
        <p className="text-gray-700">
          Our service is not intended for children under 16. We do not knowingly collect personal information from
          children under 16.
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">11. Changes to This Policy</h2>
        <p className="text-gray-700">
          We may update this Privacy Policy from time to time. We will notify you of material changes by email or
          through our service.
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">12. Contact Us</h2>
        <p className="text-gray-700">
          If you have questions about this Privacy Policy or our data practices, contact us at:
        </p>
        <div className="mt-4 rounded-lg bg-gray-50 p-4">
          <p className="font-medium text-gray-700">Email: support@scoutux.io</p>
        </div>
      </div>
    </StaticPageShell>
  );
}
